import React, { FC } from 'react';
import styled, { css } from 'styled-components/macro';

import { ColorVariants, SpaceVariants } from 'theme';
import media from 'helpers/media';

export type FontVariants =
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'h5'
    | 'h6'
    | 'bodyxl'
    | 'bodyl'
    | 'bodym'
    | 'bodys'
    | 'bodyxs';

export const VariantsStyles = {
    h1: css`
        font-weight: 300;
        font-size: 4.5rem;
        line-height: 120%;

        ${media.mobile`
            font-size: 3rem;
        `}
    `,
    h2: css`
        font-weight: 300;
        font-size: 3rem;
        line-height: 120%;

        ${media.mobile`
            font-size: 2.25rem;
        `}
    `,
    h3: css`
        font-weight: 500;
        font-size: 2.25rem;
        line-height: 120%;

        ${media.mobile`
            font-size: 1.5rem;
        `}
    `,
    h4: css`
        font-weight: 500;
        font-size: 1.5rem;
        line-height: 120%;

        ${media.mobile`
            font-size: 1.25rem;
        `}
    `,
    h5: css`
        font-weight: 500;
        font-size: 1.25rem;
        line-height: 120%;
        ${media.mobile`
            font-size: 1rem;
        `}
    `,
    h6: css`
        font-size: 1rem;
        line-height: 120%;
    `,
    bodyxl: css`
        font-size: 1.25rem;
        line-height: 150%;
    `,
    bodyl: css`
        font-size: 1.125rem;
        line-height: 150%;
    `,
    bodym: css`
        font-size: 1rem;
        line-height: 150%;
    `,
    bodys: css`
        font-size: 0.875rem;
        line-height: 150%;
    `,
    bodyxs: css`
        font-size: 0.75rem;
        line-height: 150%;
    `,
};

const Text = styled.p<{
    $align?: 'left' | 'right' | 'center' | 'justify';
    $noWrap?: boolean;
    $spaceBottom?: SpaceVariants;
    $spaceTop?: SpaceVariants;
    $color?: ColorVariants;
    $transform?: 'uppercase' | 'capitalize' | 'none';
    $fontWeight?: 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900;
    $linkType?: boolean;
    $width100?: boolean;
    $variant: FontVariants;
}>`
    display: block;
    margin: 0;
    text-decoration: none;
    text-align: ${({ $align }) => ($align ? $align : 'inherit')};

    ${({ $variant }) => VariantsStyles[$variant]}

    ${({ theme, $spaceBottom }) =>
        $spaceBottom && `margin-bottom: ${theme.space[$spaceBottom]}`};

    ${({ theme, $spaceTop }) =>
        $spaceTop && `margin-top: ${theme.space[$spaceTop]}`};

    ${({ $noWrap }) =>
        $noWrap &&
        css`
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        `};

    ${({ $transform }) =>
        $transform &&
        css`
            text-transform: ${$transform};
        `};

    ${({ $color, theme }) =>
        $color &&
        css`
            color: ${theme.colors[$color]};
        `};

    ${({ theme, $linkType }) =>
        $linkType &&
        css`
            cursor: pointer;
            font-weight: 700;
            text-decoration: underline;
            &:hover {
                color: ${theme.colors.brand};
            }
        `};

    ${({ $fontWeight }) =>
        $fontWeight &&
        css`
            font-weight: ${$fontWeight};
        `};

    ${({ $width100 }) =>
        $width100 &&
        css`
            width: 100%;
        `}
`;

const FONT_VARIANT_IN_HTML = {
    h1: 'h1',
    h2: 'h2',
    h3: 'h3',
    h4: 'h4',
    h5: 'h5',
    h6: 'h6',
    bodyxl: 'p',
    bodyl: 'p',
    bodym: 'p',
    bodys: 'p',
    bodyxs: 'p',
};

export type TypographyProps = {
    /** Set the text-align on the component */
    align?: 'left' | 'right' | 'center' | 'justify';

    /** Applies the theme typography styles */
    variant: FontVariants;

    /** If `true` the text will not wrap, but instead will truncate with a text overflow ellipsis (Can only happen with block or inline-block element with a width) */
    noWrap?: boolean;

    /** Set the margin-bottom on the component following spacing in the theme */
    spaceBottom?: SpaceVariants;
    spaceTop?: SpaceVariants;

    /** Color for the component  */
    color?: ColorVariants;

    /** Set the text-transform on the component */
    transform?: 'uppercase' | 'capitalize' | 'none';

    fontWeight?: 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900;

    linkType?: boolean;

    width100?: boolean;
};

const Typography: FC<
    TypographyProps & React.HTMLAttributes<HTMLBaseElement>
> = ({
    align,
    variant,
    noWrap,
    spaceBottom,
    spaceTop,
    color,
    transform,
    fontWeight,
    linkType,
    width100,
    ...rest
}) => {
    const as = FONT_VARIANT_IN_HTML[variant];

    return (
        <Text
            //@ts-ignore (that's hell to cast)
            as={as}
            $align={align}
            $variant={variant}
            $noWrap={noWrap}
            $spaceBottom={spaceBottom}
            $spaceTop={spaceTop}
            $color={color}
            $transform={transform}
            $fontWeight={fontWeight}
            $linkType={linkType}
            $width100={width100}
            {...rest}
        >
            {rest.children}
        </Text>
    );
};

export default Typography;
